import { useState, useEffect, useRef, useCallback } from 'react';
import { getSales, createSales, updateSale, deleteSale, getSuppliers, uploadImage, getUnits } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Plus, Save, X, Tag, Upload, Trash2, Pencil, Search, ArrowUpDown, ChevronUp, ChevronDown } from 'lucide-react';

const emptyRow = { name: '', origin: '', spec: '', price: '', unit: '個', supplier_id: '', image_url: '', description: '' };
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// 画像URLをフルURLに変換（iPhone Safari対応）
const getFullImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API_URL}${url}`;
};

export const Sales = () => {
  const [sales, setSales] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newRows, setNewRows] = useState([{ ...emptyRow }]);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const fileInputRef = useRef({});

  useEffect(() => {
    fetchData();
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const checkMobile = () => {
    setIsMobile(window.innerWidth < 768);
  };

  const fetchData = async () => {
    try {
      const [salesRes, suppliersRes, unitsRes] = await Promise.all([getSales(), getSuppliers(), getUnits()]);
      setSales(salesRes.data);
      setSuppliers(suppliersRes.data);
      setUnits(unitsRes.data);
    } catch (error) {
      toast.error('データの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleNewRowChange = useCallback((index, field, value) => {
    setNewRows(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  }, []);

  const addNewRow = () => {
    setNewRows([...newRows, { ...emptyRow }]);
  };

  const removeNewRow = (index) => {
    if (newRows.length > 1) {
      setNewRows(newRows.filter((_, i) => i !== index));
    }
  };

  const handleFileUpload = async (index, file, isEdit = false) => {
    if (!file) return;
    try {
      const url = await uploadImage(file);
      if (isEdit) {
        setEditData({ ...editData, image_url: url });
      } else {
        handleNewRowChange(index, 'image_url', url);
      }
      toast.success('画像をアップロードしました');
    } catch (error) {
      toast.error('画像のアップロードに失敗しました');
    }
  };

  const handleSaveNew = async () => {
    const validRows = newRows.filter(r => r.name && r.price);
    if (validRows.length === 0) {
      toast.error('商品名と価格は必須です');
      return;
    }

    setSaving(true);
    try {
      const items = validRows.map(r => ({
        ...r,
        price: parseFloat(r.price),
        supplier_id: r.supplier_id || null,
        image_url: r.image_url || null,
        description: r.description || '',
      }));
      await createSales(items);
      toast.success(`${items.length}件の特売商品を登録しました`);
      setNewRows([{ ...emptyRow }]);
      fetchData();
    } catch (error) {
      toast.error('登録に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (sale) => {
    setEditingId(sale.id);
    setEditData({ ...sale });
    if (isMobile) {
      setEditDialogOpen(true);
    }
  };

  // ダブルクリックで編集開始
  const handleDoubleClick = (sale) => {
    startEdit(sale);
  };

  // 検索フィルタ
  const filteredSales = sales.filter(s => {
    const query = searchQuery.toLowerCase();
    return (
      s.name?.toLowerCase().includes(query) ||
      s.origin?.toLowerCase().includes(query) ||
      s.spec?.toLowerCase().includes(query) ||
      s.supplier_name?.toLowerCase().includes(query)
    );
  });

  // ソート処理
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // ソートされたデータ
  const sortedSales = [...filteredSales].sort((a, b) => {
    if (!sortConfig.key) return 0;
    
    let aVal = a[sortConfig.key];
    let bVal = b[sortConfig.key];
    
    // null/undefined対応
    if (aVal == null) aVal = '';
    if (bVal == null) bVal = '';
    
    // 数値の場合
    if (sortConfig.key === 'price') {
      aVal = Number(aVal) || 0;
      bVal = Number(bVal) || 0;
    } else {
      aVal = String(aVal).toLowerCase();
      bVal = String(bVal).toLowerCase();
    }
    
    if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
    return 0;
  });

  // ソートアイコン
  const SortIcon = ({ column }) => {
    if (sortConfig.key !== column) {
      return <ArrowUpDown className="h-3 w-3 ml-1 opacity-30" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ChevronUp className="h-3 w-3 ml-1" />
      : <ChevronDown className="h-3 w-3 ml-1" />;
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditData({});
    setEditDialogOpen(false);
  };

  const saveEdit = async () => {
    try {
      await updateSale(editingId, {
        name: editData.name,
        origin: editData.origin,
        spec: editData.spec,
        price: parseFloat(editData.price),
        unit: editData.unit,
        supplier_id: editData.supplier_id || null,
        image_url: editData.image_url || null,
        is_active: editData.is_active,
        description: editData.description || '',
      });
      toast.success('更新しました');
      setEditingId(null);
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('更新に失敗しました');
    }
  };

  const toggleActive = async (sale) => {
    try {
      await updateSale(sale.id, { is_active: !sale.is_active });
      toast.success(sale.is_active ? '非表示にしました' : '表示にしました');
      fetchData();
    } catch (error) {
      toast.error('更新に失敗しました');
    }
  };

  const handleDelete = (sale) => {
    setSelectedSale(sale);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await deleteSale(selectedSale.id);
      toast.success('削除しました');
      setDeleteDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('削除に失敗しました');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div></div>;
  }

  return (
    <div className="space-y-6 fade-in" data-testid="sales-page">
      <div className="page-header">
        <h1 className="page-title">特売マスタ</h1>
      </div>

      {/* 新規登録エリア */}
      <Card className="border-red-200 border-2 border-dashed bg-red-50/30">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2 text-red-700">
            <Tag className="h-5 w-5" />
            特売商品 新規登録
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* モバイル用フォーム */}
          {isMobile ? (
            <div>
              {newRows.map((row, index) => (
                <Card key={`mobile-sale-${index}`} className="mb-4 border-red-200">
                  <CardContent className="p-4 space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-red-700">商品 {index + 1}</span>
                      {newRows.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => removeNewRow(index)} className="text-red-600">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs">商品名 *</Label>
                      <Input 
                        defaultValue={row.name} 
                        onBlur={(e) => handleNewRowChange(index, 'name', e.target.value)} 
                        placeholder="商品名" 
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">産地</Label>
                        <Input 
                          defaultValue={row.origin} 
                          onBlur={(e) => handleNewRowChange(index, 'origin', e.target.value)} 
                          placeholder="産地" 
                        />
                      </div>
                      <div>
                        <Label className="text-xs">規格</Label>
                        <Input 
                          defaultValue={row.spec} 
                          onBlur={(e) => handleNewRowChange(index, 'spec', e.target.value)} 
                          placeholder="規格" 
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">価格 *</Label>
                        <Input 
                          type="number" 
                          defaultValue={row.price} 
                          onBlur={(e) => handleNewRowChange(index, 'price', e.target.value)} 
                          placeholder="0" 
                        />
                      </div>
                      <div>
                        <Label className="text-xs">単位</Label>
                        <Select value={row.unit} onValueChange={(v) => handleNewRowChange(index, 'unit', v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {units.length > 0 ? (
                              units.map(u => <SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>)
                            ) : (
                              <>
                                <SelectItem value="個">個</SelectItem>
                                <SelectItem value="kg">kg</SelectItem>
                                <SelectItem value="箱">箱</SelectItem>
                              </>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">仕入れ先</Label>
                      <Select value={row.supplier_id || "none"} onValueChange={(v) => handleNewRowChange(index, 'supplier_id', v === "none" ? "" : v)}>
                        <SelectTrigger><SelectValue placeholder="選択" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">-</SelectItem>
                          {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">説明</Label>
                      <Input 
                        defaultValue={row.description} 
                        onBlur={(e) => handleNewRowChange(index, 'description', e.target.value)} 
                        placeholder="説明" 
                      />
                    </div>
                    <div>
                      <Label className="text-xs">画像</Label>
                      <div className="flex items-center gap-2">
                        <input type="file" accept="image/*" className="hidden" ref={(el) => fileInputRef.current[`new-mobile-${index}`] = el} onChange={(e) => handleFileUpload(index, e.target.files[0])} />
                        <Button variant="outline" size="sm" onClick={() => fileInputRef.current[`new-mobile-${index}`]?.click()}>
                          <Upload className="h-3 w-3 mr-1" />画像選択
                        </Button>
                        {row.image_url && <img src={row.image_url} alt="" className="h-10 w-10 object-cover rounded" loading="lazy" />}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              <div className="flex gap-2 mt-4">
                <Button variant="outline" onClick={addNewRow} className="flex-1"><Plus className="h-4 w-4 mr-1" />追加</Button>
                <Button onClick={handleSaveNew} disabled={saving} className="flex-1 bg-red-600 hover:bg-red-700"><Save className="h-4 w-4 mr-1" />{saving ? '登録中...' : '登録'}</Button>
              </div>
            </div>
          ) : (
            /* PC用テーブル */
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-2 w-28">商品名 *</th>
                      <th className="text-left p-2 w-20">産地</th>
                      <th className="text-left p-2 w-20">規格</th>
                      <th className="text-left p-2 w-16">価格 *</th>
                      <th className="text-left p-2 w-14">単位</th>
                      <th className="text-left p-2 w-28">仕入れ先</th>
                      <th className="text-left p-2 w-32">説明</th>
                      <th className="text-left p-2 w-20">画像</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {newRows.map((row, index) => (
                      <tr key={index} className="border-b">
                        <td className="p-1">
                          <Input value={row.name} onChange={(e) => handleNewRowChange(index, 'name', e.target.value)} placeholder="商品名" className="h-8" />
                        </td>
                        <td className="p-1">
                          <Input value={row.origin} onChange={(e) => handleNewRowChange(index, 'origin', e.target.value)} placeholder="産地" className="h-8" />
                        </td>
                        <td className="p-1">
                          <Input value={row.spec} onChange={(e) => handleNewRowChange(index, 'spec', e.target.value)} placeholder="規格" className="h-8" />
                        </td>
                        <td className="p-1">
                          <Input type="number" value={row.price} onChange={(e) => handleNewRowChange(index, 'price', e.target.value)} placeholder="0" className="h-8" />
                        </td>
                        <td className="p-1">
                          <Select value={row.unit} onValueChange={(v) => handleNewRowChange(index, 'unit', v)}>
                            <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {units.length > 0 ? (
                                units.map(u => <SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>)
                              ) : (
                                <>
                                  <SelectItem value="個">個</SelectItem>
                                  <SelectItem value="kg">kg</SelectItem>
                                  <SelectItem value="箱">箱</SelectItem>
                                </>
                              )}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-1">
                          <Select value={row.supplier_id || "none"} onValueChange={(v) => handleNewRowChange(index, 'supplier_id', v === "none" ? "" : v)}>
                            <SelectTrigger className="h-8"><SelectValue placeholder="選択" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">-</SelectItem>
                              {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="p-1">
                          <Input value={row.description} onChange={(e) => handleNewRowChange(index, 'description', e.target.value)} placeholder="説明" className="h-8" />
                        </td>
                        <td className="p-1">
                          <div className="flex items-center gap-1">
                            <input type="file" accept="image/*" className="hidden" ref={(el) => fileInputRef.current[`new-${index}`] = el} onChange={(e) => handleFileUpload(index, e.target.files[0])} />
                            <Button variant="outline" size="sm" className="h-8 px-2" onClick={() => fileInputRef.current[`new-${index}`]?.click()}>
                              <Upload className="h-3 w-3" />
                            </Button>
                            {row.image_url && <img src={row.image_url} alt="" className="h-8 w-8 object-cover rounded" loading="lazy" />}
                          </div>
                        </td>
                        <td className="p-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeNewRow(index)} disabled={newRows.length === 1}>
                            <X className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex gap-2 mt-4">
                <Button variant="outline" onClick={addNewRow}><Plus className="h-4 w-4 mr-1" />行を追加</Button>
                <Button onClick={handleSaveNew} disabled={saving}><Save className="h-4 w-4 mr-1" />{saving ? '登録中...' : '一括登録'}</Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* 既存特売一覧 */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-lg">登録済み特売商品 ({filteredSales.length}件)</CardTitle>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="検索..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-48"
                data-testid="sale-search"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {sortedSales.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Tag className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p>{searchQuery ? '検索結果がありません' : '特売商品がありません'}</p>
            </div>
          ) : isMobile ? (
            /* モバイル用カード表示 */
            <div className="p-4 space-y-3">
              {sortedSales.map((sale) => (
                <Card key={sale.id} className={`border ${!sale.is_active ? 'opacity-50' : ''}`} onDoubleClick={() => handleDoubleClick(sale)}>
                  <CardContent className="p-3">
                    <div className="flex items-start gap-3">
                      <div className="w-16 h-16 bg-slate-100 rounded overflow-hidden flex-shrink-0">
                        {sale.image_url ? <img src={getFullImageUrl(sale.image_url)} alt="" className="w-full h-full object-cover" loading="lazy" /> : <div className="w-full h-full flex items-center justify-center"><Tag className="h-6 w-6 text-slate-300" /></div>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-medium truncate">{sale.name}</span>
                          <Badge className={sale.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}>
                            {sale.is_active ? '表示' : '非表示'}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-500">{sale.origin || '-'} / {sale.spec || '-'}</p>
                        <p className="text-red-600 font-bold">¥{sale.price?.toLocaleString()}/{sale.unit}</p>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3 pt-3 border-t">
                      <Button variant="outline" size="sm" className="flex-1 touch-manipulation" onClick={() => toggleActive(sale)}>{sale.is_active ? '非表示' : '表示'}</Button>
                      <Button variant="outline" size="sm" className="flex-1 touch-manipulation" onClick={() => startEdit(sale)}>
                        <Pencil className="h-4 w-4 mr-1" />編集
                      </Button>
                      <Button variant="outline" size="sm" className="text-red-600 touch-manipulation" onClick={() => handleDelete(sale)}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            /* PC用テーブル表示 */
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>画像</th>
                    <th className="cursor-pointer hover:bg-slate-100 select-none" onClick={() => handleSort('name')}>
                      <div className="flex items-center">商品名<SortIcon column="name" /></div>
                    </th>
                    <th className="cursor-pointer hover:bg-slate-100 select-none" onClick={() => handleSort('origin')}>
                      <div className="flex items-center">産地<SortIcon column="origin" /></div>
                    </th>
                    <th className="cursor-pointer hover:bg-slate-100 select-none" onClick={() => handleSort('spec')}>
                      <div className="flex items-center">規格<SortIcon column="spec" /></div>
                    </th>
                    <th className="cursor-pointer hover:bg-slate-100 select-none" onClick={() => handleSort('price')}>
                      <div className="flex items-center">価格<SortIcon column="price" /></div>
                    </th>
                    <th>単位</th>
                    <th className="cursor-pointer hover:bg-slate-100 select-none" onClick={() => handleSort('supplier_name')}>
                      <div className="flex items-center">仕入れ先<SortIcon column="supplier_name" /></div>
                    </th>
                    <th>説明</th>
                    <th className="cursor-pointer hover:bg-slate-100 select-none" onClick={() => handleSort('is_active')}>
                      <div className="flex items-center">表示<SortIcon column="is_active" /></div>
                    </th>
                    <th className="text-right">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedSales.map((sale) => (
                    <tr 
                      key={sale.id} 
                      className={`${!sale.is_active ? 'opacity-50' : ''} ${editingId !== sale.id ? 'cursor-pointer hover:bg-slate-50' : ''}`}
                      onDoubleClick={() => handleDoubleClick(sale)}
                      title={editingId !== sale.id ? 'ダブルクリックで編集' : ''}
                    >
                      {editingId === sale.id ? (
                        <>
                          <td className="p-2">
                            <div className="flex items-center gap-1">
                              <input type="file" accept="image/*" className="hidden" ref={(el) => fileInputRef.current[`edit-${sale.id}`] = el} onChange={(e) => handleFileUpload(0, e.target.files[0], true)} />
                              <Button variant="outline" size="sm" className="h-8 px-2" onClick={() => fileInputRef.current[`edit-${sale.id}`]?.click()}>
                                <Upload className="h-3 w-3" />
                              </Button>
                              {editData.image_url && <img src={editData.image_url} alt="" className="h-8 w-8 object-cover rounded" loading="lazy" />}
                            </div>
                          </td>
                          <td className="p-1"><Input value={editData.name} onChange={(e) => setEditData({...editData, name: e.target.value})} className="h-8" /></td>
                          <td className="p-1"><Input value={editData.origin} onChange={(e) => setEditData({...editData, origin: e.target.value})} className="h-8" /></td>
                          <td className="p-1"><Input value={editData.spec} onChange={(e) => setEditData({...editData, spec: e.target.value})} className="h-8" /></td>
                          <td className="p-1"><Input type="number" value={editData.price} onChange={(e) => setEditData({...editData, price: e.target.value})} className="h-8 w-20" /></td>
                          <td className="p-1">
                            <Select value={editData.unit} onValueChange={(v) => setEditData({...editData, unit: v})}>
                              <SelectTrigger className="h-8 w-16"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {units.length > 0 ? (
                                  units.map(u => <SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>)
                                ) : (
                                  <>
                                    <SelectItem value="個">個</SelectItem>
                                    <SelectItem value="kg">kg</SelectItem>
                                    <SelectItem value="箱">箱</SelectItem>
                                  </>
                                )}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-1">
                            <Select value={editData.supplier_id || "none"} onValueChange={(v) => setEditData({...editData, supplier_id: v === "none" ? "" : v})}>
                              <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">-</SelectItem>
                                {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-1"><Input value={editData.description || ''} onChange={(e) => setEditData({...editData, description: e.target.value})} className="h-8" /></td>
                          <td><Switch checked={editData.is_active} onCheckedChange={(v) => setEditData({...editData, is_active: v})} /></td>
                          <td className="text-right p-2">
                            <Button size="sm" onClick={saveEdit} className="mr-1">保存</Button>
                            <Button size="sm" variant="outline" onClick={cancelEdit}>取消</Button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td>
                            <div className="w-10 h-10 bg-slate-100 rounded overflow-hidden">
                              {sale.image_url ? <img src={getFullImageUrl(sale.image_url)} alt="" className="w-full h-full object-cover" loading="lazy" /> : <div className="w-full h-full flex items-center justify-center"><Tag className="h-5 w-5 text-slate-300" /></div>}
                            </div>
                          </td>
                          <td className="font-medium">{sale.name}</td>
                          <td>{sale.origin || '-'}</td>
                          <td>{sale.spec || '-'}</td>
                          <td className="font-mono text-red-600 font-bold">¥{sale.price?.toLocaleString()}</td>
                          <td>{sale.unit}</td>
                          <td>{sale.supplier_name || '-'}</td>
                          <td className="max-w-32 truncate" title={sale.description}>{sale.description || '-'}</td>
                          <td>
                            <Badge className={sale.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}>
                              {sale.is_active ? '表示中' : '非表示'}
                            </Badge>
                          </td>
                          <td className="text-right">
                            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); toggleActive(sale); }}>{sale.is_active ? '非表示' : '表示'}</Button>
                            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); startEdit(sale); }}>編集</Button>
                            <Button variant="ghost" size="sm" className="text-red-600" onClick={(e) => { e.stopPropagation(); handleDelete(sale); }}>削除</Button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 削除確認 */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>特売商品を削除しますか？</AlertDialogTitle>
            <AlertDialogDescription>
              「{selectedSale?.name}」を削除します。この操作は取り消せません。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>キャンセル</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">削除</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* モバイル用編集ダイアログ */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>特売商品編集</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>商品名 *</Label>
              <Input 
                value={editData.name || ''} 
                onChange={(e) => setEditData({...editData, name: e.target.value})} 
                placeholder="商品名" 
              />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>産地</Label>
                <Input 
                  value={editData.origin || ''} 
                  onChange={(e) => setEditData({...editData, origin: e.target.value})} 
                  placeholder="産地" 
                />
              </div>
              <div>
                <Label>規格</Label>
                <Input 
                  value={editData.spec || ''} 
                  onChange={(e) => setEditData({...editData, spec: e.target.value})} 
                  placeholder="規格" 
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>価格 *</Label>
                <Input 
                  type="number" 
                  value={editData.price || ''} 
                  onChange={(e) => setEditData({...editData, price: e.target.value})} 
                  placeholder="0" 
                />
              </div>
              <div>
                <Label>単位</Label>
                <Select value={editData.unit || '個'} onValueChange={(v) => setEditData({...editData, unit: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {units.length > 0 ? (
                      units.map(u => <SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>)
                    ) : (
                      <>
                        <SelectItem value="個">個</SelectItem>
                        <SelectItem value="kg">kg</SelectItem>
                        <SelectItem value="箱">箱</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>仕入れ先</Label>
              <Select value={editData.supplier_id || "none"} onValueChange={(v) => setEditData({...editData, supplier_id: v === "none" ? "" : v})}>
                <SelectTrigger><SelectValue placeholder="選択" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-</SelectItem>
                  {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>説明</Label>
              <Input 
                value={editData.description || ''} 
                onChange={(e) => setEditData({...editData, description: e.target.value})} 
                placeholder="説明" 
              />
            </div>
            <div className="flex items-center gap-2">
              <Label>表示状態</Label>
              <Switch checked={editData.is_active} onCheckedChange={(v) => setEditData({...editData, is_active: v})} />
              <span className="text-sm text-slate-500">{editData.is_active ? '表示中' : '非表示'}</span>
            </div>
            <div>
              <Label>画像</Label>
              <div className="flex items-center gap-2">
                <input type="file" accept="image/*" className="hidden" ref={(el) => fileInputRef.current['edit-mobile'] = el} onChange={(e) => handleFileUpload(0, e.target.files[0], true)} />
                <Button variant="outline" size="sm" onClick={() => fileInputRef.current['edit-mobile']?.click()}>
                  <Upload className="h-3 w-3 mr-1" />画像選択
                </Button>
                {editData.image_url && <img src={editData.image_url} alt="" className="h-10 w-10 object-cover rounded" loading="lazy" />}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={cancelEdit}>キャンセル</Button>
            <Button onClick={saveEdit} className="bg-red-600 hover:bg-red-700">保存</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
